import React from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { RadioButton } from "primereact/radiobutton";
import { ProgressSpinner } from "primereact/progressspinner";
import CustomeButton from "~/components/Button";
import type {
  AccountManagerFormData,
  AccountManagerOption,
} from "~/Pages/Tenants/types/tenants";

interface AccountManagerSectionProps {
  data: AccountManagerFormData;
  onChange: (updates: Partial<AccountManagerFormData>) => void;
  onBlur?: (fieldName: string) => void;
  errors: Record<string, string[]>;
  touchedFields?: Set<string>;
  disabled?: boolean;
  accountManagers?: AccountManagerOption[];
  onAddNewAccountManager?: () => void;
  onDropdownShow?: () => void;
  onAccountManagerSelect?: (manager: AccountManagerOption) => void;
  isLoading?: boolean;
}

/**
 * Account Manager Section for Step 2
 */
export const AccountManagerSection: React.FC<AccountManagerSectionProps> = ({
  data,
  onChange,
  onBlur,
  errors,
  touchedFields = new Set(),
  disabled = false,
  accountManagers = [],
  onAddNewAccountManager,
  onDropdownShow,
  onAccountManagerSelect,
  isLoading = false,
}) => {
  const handleFieldBlur = (field: keyof AccountManagerFormData) => {
    if (onBlur) {
      onBlur(field);
    }
  };

  // Only show errors for touched fields
  const getFieldError = (fieldName: string) => {
    if (touchedFields.has(fieldName) && errors[fieldName]) {
      return errors[fieldName];
    }
    return undefined;
  };

  // Handle account manager selection
  const handleAccountManagerSelect = (managerId: string) => {
    const selectedManager = accountManagers.find((m) => m.id === managerId);
    if (selectedManager) {
      onChange({
        account_manager_id: selectedManager.id,
        account_manager_email: selectedManager.email,
        is_new_account_manager: false,
      });
      // Notify parent for toast notification
      if (onAccountManagerSelect) {
        onAccountManagerSelect(selectedManager);
      }
    }
  };

  // Prepare dropdown options
  const accountManagerOptions = accountManagers.map((manager) => ({
    label: manager.name,
    value: manager.id,
  }));

  // Custom item template with radio button style
  const itemTemplate = (option: { label: string; value: string }) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={data.account_manager_id === option.value}
          onChange={() => {}}
          tabIndex={-1}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  // Custom panel footer with "Not listed?" option
  const panelFooterTemplate = () => {
    return (
      <div className="account-manager-panel-footer">
        <span className="not-listed-text">Not listed?</span>
        <CustomeButton
          label="Add New Account Manager"
          icon="pi pi-user-plus"
          iconPos="right"
          onClick={onAddNewAccountManager}
          className="p-button-outlined p-button-sm"
          disabled={disabled}
          type="button"
        />
      </div>
    );
  };

  return (
    <div className="formgrid grid">
      <div className="field col-12">
        <p className="sub-heading">Account Manager</p>
      </div>

      <div className="field col-12">
        <label htmlFor="account_manager_id" className="input-label mb-2">
          Account manager *
        </label>
        <div className="flex gap-3 align-items-center">
          <div
            className={`account-manager-dropdown-wrapper ${isLoading ? "loader__active" : ""}`}
          >
            <Dropdown
              id="account_manager_id"
              name="account_manager_id"
              value={data.account_manager_id || null}
              onChange={(e) => handleAccountManagerSelect(e.value)}
              onBlur={() => handleFieldBlur("account_manager_id")}
              onShow={() => {
                console.log(
                  "Dropdown onShow triggered, onDropdownShow:",
                  typeof onDropdownShow,
                );
                if (onDropdownShow) {
                  console.log("Calling onDropdownShow");
                  onDropdownShow();
                } else {
                  console.log("onDropdownShow is not defined");
                }
              }}
              options={accountManagerOptions}
              optionLabel="label"
              optionValue="value"
              className={`account-manager-dropdown ${getFieldError("account_manager_id") ? "p-invalid" : ""}`}
              disabled={disabled}
              placeholder="Select existing account manager"
              appendTo={document.body}
              tabIndex={1}
              filter
              filterPlaceholder=""
              showFilterClear={false}
              itemTemplate={itemTemplate}
              panelFooterTemplate={panelFooterTemplate}
              panelClassName={`account-manager-panel ${isLoading ? "account-manager-panel-loading" : ""}`}
              emptyMessage="No Account Manager available yet"
              emptyFilterMessage="No Account Managers found."
              filterInputAutoFocus={false}
              aria-describedby={
                getFieldError("account_manager_id")
                  ? "account_manager_id-error"
                  : undefined
              }
              aria-invalid={!!getFieldError("account_manager_id")}
              aria-required="true"
              aria-label="Account Manager"
            />
            {isLoading && (
              <div className="account-manager-loading-overlay">
                <ProgressSpinner />
              </div>
            )}
          </div>
          <span className="or-text">Or</span>
          <CustomeButton
            label="Add New Account Manager"
            icon="pi pi-user-plus"
            iconPos="right"
            onClick={onAddNewAccountManager}
            className="p-button-outlined add-account-manager-btn"
            disabled={disabled}
            type="button"
          />
        </div>
        {getFieldError("account_manager_id") && (
          <small id="account_manager_id-error" className="p-error" role="alert">
            {getFieldError("account_manager_id")![0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="account_manager_email" className="input-label mb-2">
          Email address *
        </label>
        <div style={{ position: "relative", width: "100%" }}>
          <InputText
            id="account_manager_email"
            name="account_manager_email"
            value={data.account_manager_email}
            readOnly
            className={`w-full account-manager-email-readonly ${getFieldError("account_manager_email") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Enter email address"
            type="email"
            autoComplete="off"
            tabIndex={-1}
            aria-describedby={
              getFieldError("account_manager_email")
                ? "account_manager_email-error"
                : undefined
            }
            aria-invalid={!!getFieldError("account_manager_email")}
            aria-required="true"
            style={{
              paddingRight: "40px",
              backgroundColor: "#F3F4F6",
              color: "#6B7280",
              cursor: "default",
              pointerEvents: "none",
            }}
          />
          <i
            className="pi pi-lock"
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-color-secondary)",
              pointerEvents: "none",
            }}
          />
        </div>
        {getFieldError("account_manager_email") && (
          <small
            id="account_manager_email-error"
            className="p-error"
            role="alert"
          >
            {getFieldError("account_manager_email")![0]}
          </small>
        )}
      </div>
    </div>
  );
};
